import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { 
  CheckSquare, Clock, CheckCircle2, AlertCircle, Play, Plus,
  Calendar, Building2, Users, ChevronRight, Filter, Trash2, Edit
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { formatDate } from '@/utils/dateFormat';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  assigned: { label: 'Assigned', color: 'bg-blue-100 text-blue-700', icon: CheckSquare },
  in_progress: { label: 'In Progress', color: 'bg-purple-100 text-purple-700', icon: Play },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
};

export default function AdminActivities() {
  const [activities, setActivities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  
  // Form state
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project_id: '',
    vendor_id: '',
    due_date: '',
    linked_task_ids: []
  });
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [activitiesRes, projectsRes, usersRes, tasksRes] = await Promise.all([
        axios.get(`${API}/admin/vendor-activities`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/users`),
        axios.get(`${API}/tasks`)
      ]);
      
      setActivities(activitiesRes.data);
      setProjects(projectsRes.data);
      setVendors(usersRes.data.filter(u => u.role === 'vendor'));
      setTasks(tasksRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingActivity(null);
    setFormData({
      name: '',
      description: '',
      project_id: '',
      vendor_id: '',
      due_date: '',
      linked_task_ids: []
    });
    setShowCreateSheet(true);
  };

  const handleEdit = (activity) => {
    setEditingActivity(activity);
    setFormData({
      name: activity.name || '',
      description: activity.description || '',
      project_id: activity.project_id || '',
      vendor_id: activity.vendor_id || '',
      due_date: activity.due_date ? new Date(activity.due_date).toISOString().split('T')[0] : '',
      linked_task_ids: activity.linked_task_ids || []
    });
    setShowCreateSheet(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.project_id) {
      toast.error('Activity name and project are required');
      return;
    }
    
    try {
      const payload = {
        ...formData,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null
      };
      
      if (editingActivity) {
        await axios.put(`${API}/admin/vendor-activities/${editingActivity.id}`, payload);
        toast.success('Activity updated');
      } else {
        await axios.post(`${API}/admin/vendor-activities`, payload);
        toast.success('Activity created');
      }
      
      setShowCreateSheet(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save activity');
    }
  };

  const handleApprove = async (activityId) => {
    try {
      await axios.put(`${API}/admin/vendor-activities/${activityId}`, { status: 'approved' });
      toast.success('Activity approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve activity');
    }
  };

  const handleDelete = async () => {
    if (!activityToDelete) return;
    
    try {
      await axios.delete(`${API}/admin/vendor-activities/${activityToDelete.id}`);
      toast.success('Activity deleted');
      setDeleteDialogOpen(false);
      setActivityToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete activity');
    }
  };

  const handleTaskToggle = (taskId) => {
    setFormData(prev => ({
      ...prev,
      linked_task_ids: prev.linked_task_ids.includes(taskId)
        ? prev.linked_task_ids.filter(id => id !== taskId)
        : [...prev.linked_task_ids, taskId]
    }));
  };

  // Filter activities
  const filteredActivities = activities.filter(activity => {
    if (statusFilter !== 'all' && activity.status !== statusFilter) return false;
    if (projectFilter !== 'all' && activity.project_id !== projectFilter) return false;
    return true;
  });

  // Get tasks for selected project
  const projectTasks = tasks.filter(t => t.project_id === formData.project_id);

  // Count by status
  const statusCounts = {
    all: activities.length,
    pending: activities.filter(a => a.status === 'pending').length,
    assigned: activities.filter(a => a.status === 'assigned').length,
    in_progress: activities.filter(a => a.status === 'in_progress').length,
    completed: activities.filter(a => a.status === 'completed').length,
    approved: activities.filter(a => a.status === 'approved').length,
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-stone-400">Loading activities...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-activities-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-manrope text-3xl md:text-4xl font-bold tracking-tight text-stone-900 mb-2">
              Vendor Activities
            </h1>
            <p className="text-stone-500">Manage vendor-facing work assignments</p>
          </div>
          <Button onClick={handleCreate} className="apple-button" data-testid="create-activity-btn">
            <Plus size={18} className="mr-2" />
            Create Activity
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <Tabs value={statusFilter} onValueChange={setStatusFilter} className="flex-1">
            <TabsList className="grid grid-cols-6 w-full">
              <TabsTrigger value="all">All ({statusCounts.all})</TabsTrigger>
              <TabsTrigger value="pending">Pending ({statusCounts.pending})</TabsTrigger>
              <TabsTrigger value="assigned">Assigned ({statusCounts.assigned})</TabsTrigger>
              <TabsTrigger value="in_progress">In Progress ({statusCounts.in_progress})</TabsTrigger>
              <TabsTrigger value="completed">Completed ({statusCounts.completed})</TabsTrigger>
              <TabsTrigger value="approved">Approved ({statusCounts.approved})</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-48">
              <Filter size={14} className="mr-2" />
              <SelectValue placeholder="Filter by Project" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Activities List */}
        {filteredActivities.length === 0 ? (
          <div className="apple-card p-16 text-center">
            <CheckSquare size={48} className="mx-auto text-stone-300 mb-4" />
            <p className="text-stone-500">No activities found</p>
            <Button onClick={handleCreate} variant="outline" className="mt-4">
              <Plus size={16} className="mr-2" /> Create First Activity
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredActivities.map((activity) => {
              const statusConfig = STATUS_CONFIG[activity.status] || STATUS_CONFIG.pending;
              const StatusIcon = statusConfig.icon;
              const isOverdue = activity.due_date && new Date(activity.due_date) < new Date() && 
                               !['completed', 'approved'].includes(activity.status);

              return (
                <div 
                  key={activity.id} 
                  className="apple-card p-6 group hover:shadow-md transition-shadow"
                  data-testid={`admin-activity-${activity.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      {/* Activity Header */}
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-manrope text-lg font-bold text-stone-900">
                          {activity.name}
                        </h3>
                        <Badge className={statusConfig.color}>
                          <StatusIcon size={12} className="mr-1" />
                          {statusConfig.label}
                        </Badge>
                        {isOverdue && (
                          <Badge className="bg-red-100 text-red-700">
                            <AlertCircle size={12} className="mr-1" />
                            Overdue
                          </Badge>
                        )}
                      </div>

                      {/* Activity Details */}
                      <div className="flex flex-wrap gap-4 text-sm text-stone-500">
                        {activity.project_name && (
                          <span className="flex items-center gap-1">
                            <Building2 size={14} />
                            {activity.project_name}
                          </span>
                        )}
                        {activity.vendor_name && (
                          <span className="flex items-center gap-1">
                            <Users size={14} />
                            {activity.vendor_name}
                          </span>
                        )}
                        {activity.due_date && (
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            Due: {formatDate(new Date(activity.due_date))}
                          </span>
                        )}
                        {activity.linked_tasks_count > 0 && (
                          <span className="flex items-center gap-1">
                            <CheckSquare size={14} />
                            {activity.linked_tasks_count} linked task(s)
                          </span>
                        )}
                      </div>

                      {/* Progress Bar - Computed from linked tasks */}
                      {activity.linked_tasks_count > 0 && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-xs text-stone-500 mb-1">
                            <span>Avg. Progress from {activity.linked_tasks_count} task(s)</span>
                            <span className="font-semibold">{activity.computed_progress || 0}%</span>
                          </div>
                          <div className="w-full bg-stone-200 rounded-full h-2.5">
                            <div 
                              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                              style={{ width: `${activity.computed_progress || 0}%` }}
                            ></div>
                          </div>
                        </div>
                      )}

                      {/* Description */}
                      {activity.description && (
                        <p className="text-sm text-stone-600 mt-2 line-clamp-2">
                          {activity.description}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {activity.status === 'completed' && (
                        <Button 
                          size="sm" 
                          onClick={() => handleApprove(activity.id)}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <CheckCircle2 size={14} className="mr-1" /> Approve
                        </Button>
                      )}
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEdit(activity)}
                      >
                        <Edit size={14} className="mr-1" /> Edit
                      </Button>
                      
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setActivityToDelete(activity);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create/Edit Sheet */}
        <Sheet open={showCreateSheet} onOpenChange={setShowCreateSheet}>
          <SheetContent className="sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="font-manrope text-xl">
                {editingActivity ? 'Edit Activity' : 'Create Vendor Activity'}
              </SheetTitle>
            </SheetHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6 mt-6">
              <div className="space-y-2">
                <Label htmlFor="name">Activity Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Install Kitchen Cabinets"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the work to be done..."
                  rows={3}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="project">Project *</Label>
                <Select 
                  value={formData.project_id} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, project_id: val, linked_task_ids: [] }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="vendor">Assign to Vendor</Label>
                <Select 
                  value={formData.vendor_id || 'none'} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, vendor_id: val === 'none' ? '' : val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
              
              {/* Link Tasks Section */}
              {formData.project_id && projectTasks.length > 0 && (
                <div className="space-y-2">
                  <Label>Link Internal Tasks (Optional)</Label>
                  <p className="text-xs text-stone-500 mb-2">
                    Select internal tasks to bundle into this vendor activity. Vendor will NOT see task details.
                  </p>
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-2">
                    {projectTasks.map(task => (
                      <label 
                        key={task.id} 
                        className="flex items-center gap-2 p-2 hover:bg-stone-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.linked_task_ids.includes(task.id)}
                          onChange={() => handleTaskToggle(task.id)}
                          className="rounded"
                        />
                        <span className="text-sm">{task.title}</span>
                        <Badge variant="outline" className="text-xs ml-auto">
                          {task.status}
                        </Badge>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowCreateSheet(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 apple-button">
                  {editingActivity ? 'Update Activity' : 'Create Activity'}
                </Button>
              </div>
            </form>
          </SheetContent>
        </Sheet>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Activity</DialogTitle>
            </DialogHeader>
            <p className="text-stone-600">
              Are you sure you want to delete "{activityToDelete?.name}"? This action cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
